import { useState, useEffect } from "react";
import { supabase } from "@/infra/supabase/client";
import { useToast } from "@/controllers/hooks/use-toast";
import { logger } from "@/utils/logger";

interface DashboardData {
  user: {
    full_name: string;
    level: number;
    xp: number;
    total_progress_percentage: number;
    streak_days: number;
    avatar_url?: string;
  } | null;
  temples: Array<{
    id: number;
    name: string;
    description: string;
    icon: string;
    theme_color: string;
    order_number: number;
    total_tasks: number;
    progress_percentage: number;
    status: "locked" | "unlocked" | "in_progress" | "completed";
    is_featured?: boolean;
    xp_reward: number;
    created_at: string;
    active: boolean;
    order: number;
  }>;
  notifications: {
    unread_count: number;
    recent_reminders: Array<{
      title: string;
      message: string;
    }>;
  };
  achievements: Array<{
    name: string;
    description: string;
    icon: string;
    earned_at: string;
  }>;
  epicGoal: {
    title: string;
    deadline_weeks: number;
    progress_percentage: number;
    status: string;
  };
}

export const useDashboardData = () => {
  const [data, setData] = useState<DashboardData>({
    user: null,
    temples: [],
    notifications: { unread_count: 0, recent_reminders: [] },
    achievements: [],
    epicGoal: {
      title: "Conquista do SIM",
      deadline_weeks: 7,
      progress_percentage: 67,
      status: "Em progresso",
    },
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadDashboardData = async () => {
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        setLoading(false);
        return;
      }

      // Carregar todos os dados em paralelo para melhor performance
      const [
        { data: profile, error: profileError },
        { data: allTemples, error: templesError },
        { data: templeProgress, error: progressError },
        { data: notifications },
      ] = await Promise.all([
        // Query 1: Perfil do usuário
        supabase.from("profiles").select("*").eq("id", authUser.id).single(),

        // Query 2: Todos os templos ativos
        supabase
          .from("temples")
          .select("*")
          .eq("active", true)
          .order("order_number"),

        // Query 3: Progresso dos templos
        supabase.from("temple_progress").select("*").eq("user_id", authUser.id),

        // Query 4: Notificações não lidas
        supabase
          .from("notifications")
          .select("*")
          .eq("user_id", authUser.id)
          .eq("read", false)
          .order("created_at", { ascending: false })
          .limit(5),
      ]);

      if (profileError) {
        logger.error("dashboard", "Erro ao carregar perfil", {
          error: profileError,
        });
      }

      if (!profile) {
        logger.error("dashboard", "Perfil não encontrado", {
          userId: authUser.id,
        });
        throw new Error("Perfil não encontrado");
      }

      // Usar os campos level e xp
      const userLevel = profile?.level ?? 1;
      const userXp = profile?.xp ?? 0;
      const userProgress = Number(profile?.total_progress_percentage ?? 0);
      const userStreak = profile?.streak_days ?? 0;

      // Carregar conquistas recentes - Simplificado para evitar erros
      let achievements = [];

      try {
        const { data: userAchievements, error: achievementsError } =
          await supabase
            .from("user_achievements")
            .select("id, earned_at, achievement_id")
            .eq("user_id", authUser.id)
            .eq("unlocked", true)
            .order("earned_at", { ascending: false })
            .limit(3);

        if (achievementsError) {
          logger.error("dashboard", "Erro ao carregar user achievements", {
            error: achievementsError,
          });
        }

        if (userAchievements && userAchievements.length > 0) {
          const achievementIds = userAchievements.map(
            (ua) => ua.achievement_id,
          );

          // Buscar achievements em paralelo
          const achievementResults = await Promise.all(
            achievementIds.map((id) =>
              supabase
                .from("achievements")
                .select("id, name, description, icon, type, rarity")
                .eq("id", id)
                .single(),
            ),
          );

          const achievementDetails = achievementResults
            .filter((result) => result.data)
            .map((result) => result.data);

          // Combinar os dados
          if (achievementDetails.length > 0) {
            achievements = userAchievements.map((ua) => ({
              ...ua,
              achievements: achievementDetails.find(
                (a) => a.id === ua.achievement_id,
              ),
            }));
          }
        }
      } catch (error) {
        logger.error("dashboard", "Erro ao carregar achievements", { error });
      }

      // Criar um mapa de progresso por temple_id
      const progressMap = new Map(
        templeProgress?.map((tp) => [tp.temple_id, tp]) || [],
      );

      // Processar todos os templos, combinando com o progresso do usuário
      const processedTemples =
        allTemples?.map((temple, index: number) => {
          const userProgress = progressMap.get(temple.id);

          // Calcular progresso
          const currentTasks = userProgress?.completed_tasks || 0;
          const totalTasks = temple.total_tasks || 1;
          const progress = (currentTasks / totalTasks) * 100;

          // Determinar status do templo
          let status: "locked" | "unlocked" | "in_progress" | "completed" =
            "locked";

          if (userProgress?.status === "completed") {
            status = "completed";
          } else if (currentTasks > 0) {
            status = "in_progress";
          } else if (index === 0) {
            // Primeiro templo sempre desbloqueado
            status = "unlocked";
          } else {
            // Verificar se o templo anterior foi completado
            const previousTemple = allTemples[index - 1];
            const previousProgress = progressMap.get(previousTemple?.id);
            if (previousProgress?.status === "completed") {
              status = "unlocked";
            }
          }

          return {
            id: temple.id,
            name: temple.name,
            description: temple.description,
            icon: temple.icon || "target",
            theme_color: temple.theme_color || "#6366f1",
            order_number: temple.order_number || index + 1,
            total_tasks: totalTasks,
            progress_percentage: progress,
            status,
            is_featured: false,
            xp_reward: temple.total_xp || 0,
            created_at: temple.created_at || new Date().toISOString(),
            active: temple.active ?? true,
            order: temple.order_number || index + 1,
          };
        }) || [];

      // Encontrar templo em destaque (o primeiro templo em progresso ou desbloqueado)
      const featuredTemple =
        processedTemples.find((t) => t.status === "in_progress") ||
        processedTemples.find((t) => t.status === "unlocked") ||
        processedTemples[0];

      if (featuredTemple) {
        featuredTemple.is_featured = true;
      }

      const userData = profile
        ? {
            full_name: profile.full_name || "Usuário",
            level: userLevel,
            xp: userXp,
            total_progress_percentage: userProgress,
            streak_days: userStreak,
            avatar_url: profile.avatar_url,
          }
        : null;

      setData({
        user: userData,
        temples: processedTemples,
        notifications: {
          unread_count: notifications?.length || 0,
          recent_reminders:
            notifications?.slice(0, 2).map((n) => ({
              title: n.title,
              message: n.message,
            })) || [],
        },
        achievements:
          achievements?.map((ua) => ({
            name: ua.achievements.name,
            description: ua.achievements.description,
            icon: ua.achievements.icon,
            earned_at: ua.earned_at,
          })) || [],
        epicGoal: {
          title: "Conquista do SIM",
          deadline_weeks: 7,
          progress_percentage: userProgress,
          status: "Em progresso",
        },
      });
    } catch (error) {
      logger.error("dashboard", "Erro ao carregar dashboard", { error });
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do dashboard.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revalida ao voltar o foco/aba, evitando estado defasado sem reload manual
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === "visible") {
        loadDashboardData();
      }
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, refetch: loadDashboardData };
};
